import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ExportFormatEnum } from '@g99-product-web-ui/shared-interface-lib';
import {
  CheckIconSvg,
  EXPORT_OPTIONS,
} from '@g99-product-web-ui/shared-util-lib';

export interface IExportColumn {
  label: string;
  value: string;
  icon?: string;
  disabled?: boolean;
}

@Component({
  selector: 'lib-shared-export-modal',
  templateUrl: './shared-export-modal.component.html',
  styleUrl: './shared-export-modal.component.scss',
  standalone: false,
})
export class SharedExportModalComponent implements OnInit, OnChanges {
  @Input() public isOpen = false;
  @Input() public isLoading = false;
  @Input() public defaultFormat: ExportFormatEnum = ExportFormatEnum.PDF;
  @Input() public availableFormats: ExportFormatEnum[] = [
    ExportFormatEnum.PDF,
    ExportFormatEnum.EXCEL,
  ];
  @Input() public columns: IExportColumn[] = [];
  @Input() public selectAllByDefault = true;

  @Output() public export = new EventEmitter<any>();
  @Output() public close = new EventEmitter<void>();

  protected exportOptions = EXPORT_OPTIONS;
  protected selectedFormat!: ExportFormatEnum;
  protected tickIcon = CheckIconSvg;
  protected selectedColumns: Set<string> = new Set<string>();

  ngOnInit(): void {
    this.selectedFormat = this.defaultFormat;
    this._initializeSelection();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['columns'] ||
      (changes['isOpen'] && changes['isOpen'].currentValue)
    ) {
      this._initializeSelection();
    }
  }

  protected selectOption(format: ExportFormatEnum): void {
    this.selectedFormat = format;
  }

  protected isSelected(format: ExportFormatEnum): boolean {
    return this.selectedFormat === format;
  }

  protected isFormatAvailable(format: ExportFormatEnum): boolean {
    return this.availableFormats.includes(format);
  }

  protected get isAllColumnsSelected(): boolean {
    if (!this.columns || this.columns.length === 0) {
      return false;
    }
    return this.selectedColumns.size === this.columns.length;
  }

  protected get isIndeterminate(): boolean {
    if (!this.columns || this.columns.length === 0) {
      return false;
    }
    return (
      this.selectedColumns.size > 0 &&
      this.selectedColumns.size < this.columns.length
    );
  }

  protected toggleSelectAll(): void {
    if (this.isAllColumnsSelected) {
      this.selectedColumns.clear();
    } else {
      this.columns.forEach((col) => {
        if (!col.disabled) {
          this.selectedColumns.add(col.value);
        }
      });
    }
  }

  protected toggleColumn(value: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.selectedColumns.has(value)) {
      this.selectedColumns.delete(value);
    } else {
      this.selectedColumns.add(value);
    }
  }

  protected isColumnSelected(value: string): boolean {
    return this.selectedColumns.has(value);
  }

  protected onExport(): void {
    if (this.columns && this.columns.length > 0) {
      this.export.emit({
        format: this.selectedFormat,
        columns: Array.from(this.selectedColumns),
      });
    } else {
      this.export.emit(this.selectedFormat);
    }
  }

  protected onClose(): void {
    this.close.emit();
  }

  private _initializeSelection(): void {
    if (this.columns && this.columns.length > 0) {
      this.selectedColumns.clear();
      if (this.selectAllByDefault) {
        this.columns.forEach((col) => {
          if (!col.disabled) {
            this.selectedColumns.add(col.value);
          }
        });
      }
    }
  }
}
